import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CalendarClock,
  Check,
  CheckSquare,
  Copy,
  Heart,
  HeartOff,
  Layers,
  Lightbulb,
  Loader2,
  MessageCircle,
  Repeat,
  Scale,
  Send,
  Sparkles,
  Trash2,
  TrendingDown,
  TrendingUp,
  Trophy,
  UserPlus,
  UserX,
  Users,
  X,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Markdown } from "@/components/ui/markdown";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { COPY_FEEDBACK_TIMEOUT_MS } from "@/lib/timing-constants";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useSolomonChat } from "@/hooks/useSolomon";
import { useSolomonContext, SolomonHint, SolomonAction } from "@/components/solomon/SolomonContext";
import { SolomonPlannerComposer } from "@/components/solomon/SolomonPlannerComposer";

/**
 * Map icon names to Lucide components
 */
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  "message-circle": MessageCircle,
  heart: Heart,
  "heart-off": HeartOff,
  users: Users,
  "user-plus": UserPlus,
  "user-x": UserX,
  trophy: Trophy,
  layers: Layers,
  "check-square": CheckSquare,
  send: Send,
  "alert-triangle": AlertTriangle,
  "trending-up": TrendingUp,
  "trending-down": TrendingDown,
  scale: Scale,
  "calendar-clock": CalendarClock,
  lightbulb: Lightbulb,
  repeat: Repeat,
};

/**
 * Get background color class based on hint type
 */
function getHintTypeStyles(type?: string): string {
  switch (type) {
    case "alert":
      return "border-destructive/40 bg-destructive/10";
    case "workflow":
      return "border-primary/40 bg-primary/10";
    case "insight":
      return "border-info-500/40 bg-info-500/10";
    default:
      return "border-border/60 bg-muted/60";
  }
}

/**
 * Get button variant based on action variant
 */
function getButtonVariant(
  variant?: SolomonAction["variant"]
): "default" | "secondary" | "destructive" | "outline" | "ghost" | "link" {
  switch (variant) {
    case "primary":
      return "default";
    case "secondary":
      return "secondary";
    case "destructive":
      return "destructive";
    default:
      return "outline";
  }
}

interface SolomonPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draftPrompt?: string | null;
  onDraftPromptConsumed?: () => void;
}

const DEFAULT_SUGGESTIONS = [
  "How do I find a person or household?",
  "Where can I update contributions?",
  "How do I track requests and tasks?",
];

function getSuggestionsForPath(pathname: string) {
  if (pathname.startsWith("/finance")) {
    return [
      "How do funds and allocations work?",
      "Where can I review recent contributions?",
      "How do I export financial reports?",
    ];
  }

  if (pathname.startsWith("/contributions")) {
    return [
      "How do I edit a contribution?",
      "How do I split a gift across funds?",
      "Where can I resend a receipt?",
    ];
  }

  if (pathname.startsWith("/requests")) {
    return [
      "How do I assign a request?",
      "What are the request statuses?",
      "How do I close a completed request?",
    ];
  }

  if (pathname.startsWith("/events") || pathname.startsWith("/calendar")) {
    return [
      "How do I record attendance?",
      "Where can I see event registrations?",
      "How do I update event details?",
    ];
  }

  if (pathname.startsWith("/community") || pathname.startsWith("/people") || pathname.startsWith("/directory")) {
    return [
      "How do I update a person's profile?",
      "How does directory privacy work?",
      "Where can I find households?",
    ];
  }

  if (pathname.startsWith("/messaging")) {
    return [
      "How do I send a message to a group?",
      "Where can I see message history?",
      "How do I manage messaging lists?",
    ];
  }

  if (pathname.startsWith("/projects")) {
    return [
      "How do I create tasks for a project?",
      "Where can I update project status?",
      "How do I assign task owners?",
    ];
  }

  return DEFAULT_SUGGESTIONS;
}

function getContextLabel(pathname: string) {
  if (pathname === "/") return "Dashboard";
  if (pathname.startsWith("/finance")) return "Finance";
  if (pathname.startsWith("/contributions")) return "Contributions";
  if (pathname.startsWith("/requests")) return "Care Requests";
  if (pathname.startsWith("/events") || pathname.startsWith("/calendar")) return "Events";
  if (pathname.startsWith("/community") || pathname.startsWith("/people") || pathname.startsWith("/directory")) return "People";
  if (pathname.startsWith("/messaging")) return "Messaging";
  if (pathname.startsWith("/projects")) return "Projects";
  return "Forge";
}

export function SolomonPanel({
  open,
  onOpenChange,
  draftPrompt,
  onDraftPromptConsumed,
}: SolomonPanelProps) {
  const { messages, isLoading, sendMessage, clearHistory } = useSolomonChat();
  const { hints, dismissHint, clearAllHints, executeAction } = useSolomonContext();
  const navigate = useNavigate();

  // Sort hints by priority
  const sortedHints = useMemo(() => {
    const priorityOrder: Record<string, number> = {
      high: 0,
      medium: 1,
      low: 2,
    };
    return [...hints].sort((a, b) => {
      const aPriority = priorityOrder[a.priority || "low"] ?? 2;
      const bPriority = priorityOrder[b.priority || "low"] ?? 2;
      return aPriority - bPriority;
    });
  }, [hints]);

  // Handle action button clicks
  const handleAction = (hint: SolomonHint, action: SolomonAction) => {
    // If action has an href, navigate to it
    if (action.href) {
      navigate(action.href);
      onOpenChange(false);
    }

    // Execute the action (this will also auto-dismiss unless action is "dismiss")
    executeAction(action.action, hint.id, hint.metadata);
  };
  const [input, setInput] = useState("");
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const suggestions = useMemo(() => getSuggestionsForPath(location.pathname), [location.pathname]);
  const contextLabel = useMemo(() => getContextLabel(location.pathname), [location.pathname]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    if (open && draftPrompt) {
      setInput(draftPrompt);
      onDraftPromptConsumed?.();
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [draftPrompt, onDraftPromptConsumed, open]);

  // Auto-scroll to the newest message (or the thinking loader) whenever
  // the conversation grows. scrollIntoView on a sentinel works inside
  // the Radix ScrollArea viewport. Mirrors AIInsightsSidebar.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const handleCopy = (content: string, index: number) => {
    navigator.clipboard.writeText(content);
    setCopiedIndex(index);
    toast.success("Response copied");
    setTimeout(() => {
      setCopiedIndex((current) => (current === index ? null : current));
    }, COPY_FEEDBACK_TIMEOUT_MS);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!input.trim()) return;
    sendMessage(input.trim());
    setInput("");
  };

  const handleSuggestion = (suggestion: string) => {
    setInput(suggestion);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col sm:max-w-md p-0" hideCloseButton>
        <SheetHeader className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between gap-3">
            <SheetTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-5 w-5 text-primary" />
              Ask Solomon
            </SheetTitle>
            <div className="flex items-center gap-2">
              {messages.length > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={clearHistory}
                  aria-label="Clear conversation"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onOpenChange(false)}
                aria-label="Close Solomon panel"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Context: {contextLabel}
          </p>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="flex flex-col gap-4 px-6 py-4">
            {sortedHints.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {sortedHints.some((h) => h.type === "workflow" || h.type === "insight")
                      ? "Suggestions & Insights"
                      : "Contextual hints"}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto px-2 py-1 text-xs"
                    onClick={clearAllHints}
                  >
                    Clear all
                  </Button>
                </div>
                <div className="space-y-2">
                  {sortedHints.map((hint) => {
                    const IconComponent = hint.icon ? iconMap[hint.icon] : null;

                    return (
                      <div
                        key={hint.id}
                        className={cn(
                          "rounded-lg border p-3 text-sm",
                          getHintTypeStyles(hint.type)
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2">
                            {IconComponent && (
                              <IconComponent className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                            )}
                            <p className="text-sm text-foreground">{hint.message}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 flex-shrink-0"
                            onClick={() => dismissHint(hint.id)}
                            aria-label="Dismiss hint"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>

                        {/* Action buttons */}
                        {hint.actions && hint.actions.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {hint.actions.map((action) => (
                              <Button
                                key={action.action}
                                variant={getButtonVariant(action.variant)}
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => handleAction(hint, action)}
                              >
                                {action.label}
                              </Button>
                            ))}
                          </div>
                        )}

                        {/* Legacy link support */}
                        {hint.link && !hint.actions && (
                          <Link
                            to={hint.link.href}
                            className="mt-2 inline-flex text-xs text-primary hover:underline"
                          >
                            {hint.link.label}
                          </Link>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/*
              Planner composer — natural-language → typed action
              proposals via the action engine. Lives ABOVE the chat
              composer so it's the user's first surface when they
              want Solomon to *do* something rather than answer a
              question. The chat composer remains at the bottom of
              the sheet for Q&A.
            */}
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Take action
              </p>
              <SolomonPlannerComposer />
            </div>

            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Suggested questions
              </p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((suggestion) => (
                  <Button
                    key={suggestion}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleSuggestion(suggestion)}
                    className="h-auto whitespace-normal text-left"
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              {messages.length === 0 && (
                <div className="rounded-lg border border-dashed border-border/60 p-4 text-center text-sm text-muted-foreground">
                  <p>Hi! I&apos;m Solomon.</p>
                  <p className="text-xs mt-2">Ask me anything about using Forge.</p>
                </div>
              )}

              {messages.map((message, index) => (
                <div
                  key={`${message.role}-${index}`}
                  className={cn(
                    "rounded-lg p-3 text-sm",
                    message.role === "user"
                      ? "ml-8 bg-primary text-primary-foreground"
                      : "mr-8 bg-muted"
                  )}
                >
                  {message.role === "assistant" ? (
                    <>
                      <Markdown>{message.content}</Markdown>
                      <div className="mt-2 flex justify-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-foreground"
                          onClick={() => handleCopy(message.content, index)}
                          aria-label="Copy response"
                        >
                          {copiedIndex === index ? (
                            <Check className="h-3.5 w-3.5" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                    </>
                  ) : (
                    message.content
                  )}
                </div>
              ))}

              {/* Only show the "thinking" bubble while waiting for the first
                  token. Once a reply starts streaming, the last message is the
                  (growing) assistant bubble, so the loader steps aside. */}
              {isLoading && messages[messages.length - 1]?.role !== "assistant" && (
                <div className="mr-8 flex items-center gap-2 rounded-lg bg-muted p-3 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Solomon is thinking…</span>
                </div>
              )}

              <div ref={bottomRef} />
            </div>
          </div>
        </ScrollArea>

        <form onSubmit={handleSubmit} className="border-t border-border px-6 py-4">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Ask a question..."
              disabled={isLoading}
            />
            <Button type="submit" size="icon" disabled={isLoading} aria-label="Submit question">
              <Sparkles className="h-4 w-4" />
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
